/**
 * `code` is the second-factor input, and it arrived with the MFA screen.
 *
 * Widening the union rather than routing a wrong TOTP code to `form`: M1-R2 is
 * about the message landing on the control that caused it, and on `/mfa` that
 * control is the code box. `FormError` renders only `form`, so nothing that
 * consumes this had to change — a `code` failure simply does not appear in the
 * form-level slot, which is the behaviour wanted.
 */
export type AuthField = 'email' | 'password' | 'code' | 'phone' | 'form'

export type FieldError = {
  field: AuthField
  message: string
  /** The raw Firebase code, for logs. Normalise with `analyticsCode` before emitting. */
  code: string
}

/**
 * The MFA pending credential expired. The challenge is dead — the code is not
 * wrong, and there is nothing to retype.
 *
 * **This key appears in no Firebase document, and that is not a typo.** The
 * server answers `mfaSignIn:finalize` with
 * `"TOTP_CHALLENGE_TIMEOUT : TOTP challenge timeout, provide first factor
 * again."`, and `@firebase/auth` carries no mapping for it. Its API layer splits
 * the message on `' : '`, misses its own `errorMap`, and then SYNTHESISES a
 * client code by lowercasing the server one:
 *
 *     errorMap[serverErrorCode] || serverErrorCode.toLowerCase().replace(/[_\s]+/g, '-')
 *
 * so `TOTP_CHALLENGE_TIMEOUT` becomes `auth/totp-challenge-timeout`. The string
 * is the transform's output rather than a documented constant, which means the
 * SDK version pins it — if Firebase ever adds a real mapping, this key stops
 * matching and the screen silently returns to the unclassified message. That is
 * what `firebaseAdapter`'s warn on an unmapped code exists to catch.
 *
 * **Named TOTP, raised for every factor.** It is the half-finished sign-in that
 * expires, not the authenticator, so an SMS user meets it under Google's TOTP
 * spelling. Observed on a phone factor.
 *
 * `form`, not `code`: the six digits may have been perfect. Naming the code box
 * would tell the user to correct something that was very likely right.
 */
export const MFA_CHALLENGE_EXPIRED = 'auth/totp-challenge-timeout'

/** The code Firebase raises when a sensitive operation needs a fresh login. */
export const REQUIRES_RECENT_LOGIN = 'auth/requires-recent-login'

/**
 * Firebase code → which input was wrong, and what to say.
 *
 * **`form` is a real answer, not a fallback bucket.** Three of these genuinely
 * belong to no field: `invalid-credential` is Firebase refusing to say which
 * half was wrong, `too-many-requests` is about the account rather than the
 * input, and `unauthorized-domain` is about our configuration. Putting any of
 * them on an input tells the user to fix something that is not broken.
 *
 * Exported so `errors.test.ts` can round-trip every key through the analytics
 * value guard — a code that cannot be emitted is a code we cannot measure.
 */
export const KNOWN_CODES: Record<string, { field: AuthField; message: string }> = {
  'auth/email-already-in-use': {
    field: 'email',
    message: 'An account already uses this address. Try signing in instead.',
  },
  'auth/invalid-email': { field: 'email', message: "That doesn't look like an email address." },
  'auth/user-not-found': { field: 'email', message: 'No account uses this address.' },
  'auth/user-disabled': {
    field: 'email',
    message: 'This account has been disabled. Contact support to reopen it.',
  },
  'auth/weak-password': {
    field: 'password',
    message: 'Use at least 6 characters.',
  },
  'auth/wrong-password': { field: 'password', message: 'That password is incorrect.' },
  'auth/missing-password': { field: 'password', message: 'Enter your password.' },

  // M1-R5. The second factor's own input — see `AuthField` for why these are
  // not `form`. `code-expired` says what to do next rather than only what went
  // wrong: a TOTP code rotates, so the recovery is to read the current one.
  'auth/invalid-verification-code': {
    field: 'code',
    message: 'That code is incorrect. Check it and try again.',
  },
  'auth/code-expired': {
    field: 'code',
    message: 'That code has expired. Enter the current one from your authenticator app.',
  },
  'auth/missing-code': {
    field: 'code',
    message: 'Enter the six-digit code.',
  },
  // #171, and raised by the adapter rather than by Firebase: a phone factor was
  // submitted with no SMS behind it. On `code` because the recovery is at the
  // code box — ask for one — and because `form` would put it above a button
  // that is already labelled with the fix.
  'auth/missing-verification-id': {
    field: 'code',
    // Names no control. The earlier copy said "choose Send code first", and
    // whenever this error is reachable the visible button reads "Send a new
    // code" — so it pointed at something that was not on screen.
    message: "We haven't sent a code to that number yet. Ask for one and we'll text it to you.",
  },

  // Genuinely form-level — see the comment above.
  'auth/invalid-credential': {
    field: 'form',
    message: 'That email and password do not match an account.',
  },
  'auth/too-many-requests': {
    field: 'form',
    message: 'Too many attempts. Wait a few minutes and try again.',
  },
  'auth/unauthorized-domain': {
    field: 'form',
    message:
      'This site is not an authorised sign-in domain for TrackE5. This is a configuration problem, not something you can fix — please report it.',
  },
  'auth/network-request-failed': {
    field: 'form',
    message: "We couldn't reach the sign-in service. Check your connection and try again.",
  },
  'auth/popup-blocked': {
    field: 'form',
    message:
      'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.',
  },
  'auth/popup-closed-by-user': {
    field: 'form',
    message: 'The sign-in window closed before you finished.',
  },
  // Re-authentication with the wrong Google account: Firebase refuses it and
  // the recovery is specific.
  'auth/user-mismatch': {
    field: 'form',
    message: 'Choose the Google account you signed in with.',
  },
  'auth/cancelled-popup-request': {
    field: 'form',
    message: 'Another sign-in window was opened. Close it and try again.',
  },
  'auth/account-exists-with-different-credential': {
    field: 'form',
    message:
      'This address is already registered with a different sign-in method. Use the one you signed up with.',
  },
  // Raised by the adapter itself, not by Firebase: `sendEmailVerification`
  // needs a user and there is no SDK code for "you passed nothing". The session
  // went away between rendering the banner and pressing Resend, and saying so
  // beats the unclassified message.
  'auth/no-current-user': {
    field: 'form',
    message: "You're not signed in any more. Sign in again and we'll send it.",
  },
  // `ResetPassword`'s two terminal codes. Both name the LINK rather than
  // anything typed — there is nothing to type yet when `verifyResetCode`
  // raises them, and a password already typed when `completeReset` raises one
  // is not what is wrong either. `form` would be the wrong choice too: this
  // screen renders these as a distinct terminal arm rather than a message
  // above a button, exactly as `MFA_CHALLENGE_EXPIRED` gets its own reasoning
  // above. Kept here rather than routed through `field: 'form'` so a future
  // caller that DOES want the plain message (there is none today) still finds
  // a correct one.
  'auth/expired-action-code': {
    field: 'form',
    message: 'This reset link has expired. Request a new one and use it within 30 minutes.',
  },
  'auth/invalid-action-code': {
    field: 'form',
    message:
      "This reset link isn't valid — it may already have been used. Request a new one.",
  },
  'auth/operation-not-allowed': {
    field: 'form',
    message: 'This sign-in method is not enabled for TrackE5. Please report it.',
  },
  // #171's delivery path. All three are about sending the SMS, not about
  // anything the user typed — there is nothing typed yet when they fire.
  'auth/captcha-check-failed': {
    field: 'form',
    message: "We couldn't confirm this browser is a real one. Reload the page and try again.",
  },
  'auth/quota-exceeded': {
    field: 'form',
    message:
      "TrackE5 has sent too many codes today. Use your authenticator app if you have one, or try again tomorrow.",
  },
  // The account has a factor whose id we did not offer, or none at all. Reached
  // by a caller bug rather than by a user, so it says what to do rather than
  // what happened.
  // Enrolment, which is a different set of failures from a challenge: these are
  // about adding a factor, not about answering one.
  'auth/requires-recent-login': {
    field: 'form',
    message:
      'For security, adding or removing two-step verification needs a fresh sign-in. Sign out and back in, then try again.',
  },
  'auth/second-factor-already-in-use': {
    field: 'form',
    message: 'That factor is already on your account.',
  },
  'auth/maximum-second-factor-count-exceeded': {
    field: 'form',
    message: "You've added as many verification methods as an account can hold. Remove one first.",
  },
  'auth/unverified-email': {
    field: 'form',
    message: 'Verify your email address before adding two-step verification.',
  },
  'auth/invalid-phone-number': {
    field: 'phone',
    message: 'Enter a phone number including its country code, like +1 555 123 4567.',
  },
  'auth/missing-phone-number': {
    field: 'phone',
    message: 'Enter the phone number to send codes to.',
  },
  'auth/multi-factor-info-not-found': {
    field: 'form',
    message: "We couldn't find that verification method on your account. Start signing in again.",
  },
  // See the constant's own docblock for where this spelling comes from. The
  // message states the recovery Firebase itself names — "provide first factor
  // again" — because the obvious wording here, "try again", is the one action
  // that is guaranteed to fail: the credential every retry would replay is the
  // thing that expired.
  [MFA_CHALLENGE_EXPIRED]: {
    field: 'form',
    message: "This sign-in took too long to finish. Sign in again and we'll send a new code.",
  },
}

const UNKNOWN =
  "We couldn't complete that. Try again, and if it keeps happening please report it."

export function authErrorFor(code: string): FieldError {
  const known = KNOWN_CODES[code]
  // No default to a field. An unrecognised code is genuinely unclassified, and
  // guessing points the user at an input that is probably fine.
  return { field: known?.field ?? 'form', message: known?.message ?? UNKNOWN, code }
}

/**
 * The code in a shape `analytics.ts` will accept.
 *
 * `IDENTIFIER` is `/^[a-z0-9][a-z0-9_.-]*$/` — the `/` in `auth/invalid-email`
 * fails it, and `emit()` THROWS in dev. So the property that exists to make
 * failures measurable would itself crash the failure screen. Stripped here, at
 * the one place that knows the code's shape.
 *
 * The `'unknown'` fallback is not defensive padding, and it covers two cases
 * rather than one. `Number('')` is `0`, so the empty string is rejected as a
 * number-in-disguise — and so is any all-digit code: `analyticsCode('auth/500')`
 * returned `'500'`, which passes IDENTIFIER and then throws in `emit()` on the
 * same rule. Both review agents found that independently. A normaliser whose
 * whole job is to keep a failure screen from crashing must not be able to
 * produce a value that crashes it, and the digit case is reachable exactly
 * where the function matters most: an UNMAPPED code, which is the one shape
 * nobody has curated.
 */
export function analyticsCode(code: string): string {
  const bare = code.replace(/^auth\//, '').toLowerCase().slice(0, 32)
  const sendable = /^[a-z0-9][a-z0-9_.-]*$/.test(bare) && Number.isNaN(Number(bare))
  return sendable ? bare : 'unknown'
}
